// Pantallas y luces: OLED (SSD1306 de 0.96″ y 0.91″, SH1106 de 1.3″), displays de 7 segmentos, módulo
// TM1637, matrices y display de 8 dígitos con MAX7219, LEDs direccionables WS2812B (NeoPixel) y barra
// de 10 LEDs.
import { definePart, PITCH } from './registry.js';
import { LED_COLORS } from './basic.js';

// Colores de los displays de LEDs (mismo voltaje directo que los LEDs sueltos).
export const SEG_COLORS = ['red', 'orange', 'yellow', 'green', 'blue', 'white'];
const segColorOptions = SEG_COLORS.map((value) => ({ value, label: LED_COLORS[value].name, color: LED_COLORS[value].hex }));
const segColorProp = (def = 'red') => ({ key: 'color', label: 'Color', type: 'select', default: def, options: segColorOptions });
const commonProp = {
  key: 'common', label: 'Terminal común', type: 'select', default: 'cathode',
  options: [{ value: 'cathode', label: 'Cátodo común (−)' }, { value: 'anode', label: 'Ánodo común (+)' }],
};

// OLED 0.96″ 128×64 ------------------------------------------------------------------------------
export const OLED = {
  W: 27.3,
  D: 27.8,
  PCB_Y: 1.5,
  PCB_T: 1.2,
  // Vidrio del panel (26.7 × 19.26 mm) y zona activa de 128×64 píxeles (21.74 × 10.86 mm)
  glass: { w: 26.7, d: 19.26, t: 1.4 },
  active: { w: 21.74, d: 10.86 },
};
OLED.TOP = OLED.PCB_Y + OLED.PCB_T;
OLED.HEADER_Z = -OLED.D / 2 + 1.65;
OLED.PIN_Y = OLED.TOP + 8.5;
OLED.glass.z0 = -OLED.D / 2 + 4.5; // borde superior del vidrio
OLED.active.z = OLED.glass.z0 + 2.2 + OLED.active.d / 2; // centro de la zona activa

export const OLED_COLORS = {
  white: { name: 'Blanca', hex: '#eef6ff' },
  blue: { name: 'Azul', hex: '#36b8ff' },
  'yellow-blue': { name: 'Amarilla y azul', hex: '#36b8ff', top: '#ffd21a' },
};

definePart({
  type: 'oled-ssd1306',
  name: 'Pantalla OLED 0.96″ I²C',
  category: 'luces',
  description: 'OLED de 128×64 píxeles con controlador SSD1306 (librerías Adafruit_SSD1306 y U8g2). SDA a A4 y SCL a A5.',
  keywords: 'oled ssd1306 pantalla display grafica i2c 128x64 u8g2 adafruit',
  props: [
    { key: 'address', label: 'Dirección I²C', type: 'select', default: '0x3C', options: [{ value: '0x3C', label: '0x3C (de fábrica)' }, { value: '0x3D', label: '0x3D (puente SA0)' }] },
    { key: 'color', label: 'Color', type: 'select', default: 'white',
      options: Object.entries(OLED_COLORS).map(([value, c]) => ({ value, label: c.name, color: c.top || c.hex })) },
  ],
  pins: ['GND', 'VCC', 'SCL', 'SDA'].map((name, i) => ({
    name,
    label: { GND: 'GND', VCC: 'VCC (3.3–5 V)', SCL: 'SCL (reloj I²C · A5 en el Uno)', SDA: 'SDA (datos I²C · A4 en el Uno)' }[name],
    kind: name === 'GND' ? 'gnd' : name === 'VCC' ? 'power' : 'signal',
    x: (i - 1.5) * PITCH,
    y: OLED.PIN_Y,
    z: OLED.HEADER_Z,
  })),
  footprint: { w: OLED.W, d: OLED.D, h: 12 },
  help: 'En la versión amarilla y azul las 16 filas de arriba son amarillas. Sin la bomba de carga interna (comando 8D 14, que mandan Adafruit_SSD1306 y U8g2) la pantalla no enciende.',
});

// OLED 1.3″ 128×64 con SH1106 ------------------------------------------------------------------------
export const OLED13 = {
  W: 35.4,
  D: 33.5,
  PCB_Y: 1.5,
  PCB_T: 1.2,
  glass: { w: 34.5, d: 23.0, t: 1.5 },
  active: { w: 29.42, d: 14.7 },
};
OLED13.TOP = OLED13.PCB_Y + OLED13.PCB_T;
OLED13.HEADER_Z = -OLED13.D / 2 + 1.65;
OLED13.PIN_Y = OLED13.TOP + 8.5;
OLED13.glass.z0 = -OLED13.D / 2 + 5.0;
OLED13.active.z = OLED13.glass.z0 + 2.4 + OLED13.active.d / 2;

definePart({
  type: 'oled-sh1106',
  name: 'Pantalla OLED 1.3″ I²C (SH1106)',
  category: 'luces',
  description: 'OLED de 128×64 más grande, con controlador SH1106: se usa con U8g2 (constructores SH1106). Ojo: el orden de las patas es VDD, GND, SCK, SDA.',
  keywords: 'oled sh1106 pantalla display grafica i2c 128x64 1.3 u8g2',
  props: [
    { key: 'address', label: 'Dirección I²C', type: 'select', default: '0x3C', options: [{ value: '0x3C', label: '0x3C' }, { value: '0x3D', label: '0x3D' }] },
    { key: 'color', label: 'Color', type: 'select', default: 'white',
      options: ['white', 'blue'].map((value) => ({ value, label: OLED_COLORS[value].name, color: OLED_COLORS[value].hex })) },
  ],
  pins: ['VCC', 'GND', 'SCL', 'SDA'].map((name, i) => ({
    name,
    label: { VCC: 'VDD (3.3–5 V)', GND: 'GND', SCL: 'SCK (reloj I²C · A5 en el Uno)', SDA: 'SDA (datos I²C · A4 en el Uno)' }[name],
    kind: name === 'GND' ? 'gnd' : name === 'VCC' ? 'power' : 'signal',
    x: (i - 1.5) * PITCH,
    y: OLED13.PIN_Y,
    z: OLED13.HEADER_Z,
  })),
  footprint: { w: OLED13.W, d: OLED13.D, h: 12 },
  help: 'El SH1106 no tiene los modos de direccionamiento horizontal y vertical del SSD1306: con Adafruit_SSD1306 la imagen sale revuelta. Con U8g2 usa p. ej. U8G2_SH1106_128X64_NONAME_F_HW_I2C.',
});

// OLED 0.91″ 128×32 ---------------------------------------------------------------------------------
export const OLED32 = {
  W: 38.0,
  D: 12.0,
  PCB_Y: 1.5,
  PCB_T: 1.2,
  glass: { w: 30.0, d: 11.5, t: 1.2, x0: -14.0 }, // x0: borde izquierdo del vidrio
  active: { w: 22.38, d: 5.58 },
};
OLED32.TOP = OLED32.PCB_Y + OLED32.PCB_T;
OLED32.HEADER_X = -OLED32.W / 2 + 1.7;
OLED32.PIN_Y = OLED32.TOP + 8.5;
OLED32.active.x = OLED32.glass.x0 + 1.6 + OLED32.active.w / 2;

definePart({
  type: 'oled-128x32',
  name: 'Pantalla OLED 0.91″ I²C',
  category: 'luces',
  description: 'OLED alargada de 128×32 píxeles con SSD1306 (Adafruit_SSD1306 con alto 32, U8g2 «128X32_UNIVISION»). SDA a A4 y SCL a A5.',
  keywords: 'oled ssd1306 pantalla display grafica i2c 128x32 0.91 u8g2 adafruit',
  props: [
    { key: 'address', label: 'Dirección I²C', type: 'select', default: '0x3C', options: [{ value: '0x3C', label: '0x3C' }, { value: '0x3D', label: '0x3D' }] },
    { key: 'color', label: 'Color', type: 'select', default: 'white',
      options: ['white', 'blue'].map((value) => ({ value, label: OLED_COLORS[value].name, color: OLED_COLORS[value].hex })) },
  ],
  pins: ['GND', 'VCC', 'SCL', 'SDA'].map((name, i) => ({
    name,
    label: { GND: 'GND', VCC: 'VCC (3.3–5 V)', SCL: 'SCK/SCL (reloj I²C · A5 en el Uno)', SDA: 'SDA (datos I²C · A4 en el Uno)' }[name],
    kind: name === 'GND' ? 'gnd' : name === 'VCC' ? 'power' : 'signal',
    x: OLED32.HEADER_X,
    y: OLED32.PIN_Y,
    z: (i - 1.5) * PITCH,
  })),
  footprint: { w: OLED32.W, d: OLED32.D, h: 11 },
  help: 'El panel tiene 32 filas cableadas en la configuración secuencial de COM (DA 02): si se inicializa como 128×64 se ve una de cada dos líneas.',
});

// Displays de 7 segmentos 0.56″ ----------------------------------------------------------------------
export const SEG_NAMES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'DP'];
const SEG_LABELS = {
  A: 'A (arriba)', B: 'B (arriba a la derecha)', C: 'C (abajo a la derecha)', D: 'D (abajo)', E: 'E (abajo a la izquierda)',
  F: 'F (arriba a la izquierda)', G: 'G (centro)', DP: 'DP (punto decimal)',
};
// Separación entre filas de patas: 0.6″ (cruzan el canal de la protoboard).
export const SEG7 = { W: 12.6, D: 19.0, H: 8.0, Y0: 0.6, ROW_Z: 3 * PITCH };
export const SEG7X4 = { W: 50.4, D: 19.0, H: 8.0, Y0: 0.6, ROW_Z: 3 * PITCH, PITCH_X: 12.6 };

const SEG7_TOP = ['G', 'F', 'COM.1', 'A', 'B'];
const SEG7_BOTTOM = ['E', 'D', 'COM.2', 'C', 'DP'];
const SEG7_NUM = { E: 1, D: 2, 'COM.2': 3, C: 4, DP: 5, B: 6, A: 7, 'COM.1': 8, F: 9, G: 10 };

function comLabel(props) {
  return props.common === 'anode' ? 'COM (ánodo común, a +)' : 'COM (cátodo común, a GND)';
}

definePart({
  type: 'seven-segment',
  name: 'Display de 7 segmentos',
  category: 'luces',
  description: 'Un dígito de 0.56″ con punto decimal: ocho LEDs con una terminal común. Cada segmento necesita su resistencia (220–470 Ω).',
  keywords: 'pantalla display 7 segmentos siete digito 5161as numero led',
  insertable: true,
  props: [commonProp, segColorProp()],
  pins: (props) => {
    const pin = (name, i, z) => ({
      name,
      label: `${SEG7_NUM[name]} · ${name.startsWith('COM') ? comLabel(props) : SEG_LABELS[name]}`,
      kind: name.startsWith('COM') ? (props.common === 'anode' ? 'power' : 'gnd') : 'signal',
      x: (i - 2) * PITCH,
      y: 0,
      z,
      insert: true,
    });
    return [...SEG7_TOP.map((n, i) => pin(n, i, -SEG7.ROW_Z)), ...SEG7_BOTTOM.map((n, i) => pin(n, i, SEG7.ROW_Z))];
  },
  buses: [['COM.1', 'COM.2']],
  footprint: { w: SEG7.W, d: SEG7.D, h: 9 },
  help: 'Las dos patas COM están unidas por dentro. Patas de arriba: G, F, COM, A, B; de abajo: E, D, COM, C, DP.',
});

const SEG7X4_TOP = ['D1', 'A', 'F', 'D2', 'D3', 'B'];
const SEG7X4_BOTTOM = ['E', 'D', 'DP', 'C', 'G', 'D4'];
const SEG7X4_NUM = { E: 1, D: 2, DP: 3, C: 4, G: 5, D4: 6, B: 7, D3: 8, D2: 9, F: 10, A: 11, D1: 12 };

definePart({
  type: 'seven-segment-4',
  name: 'Display de 7 segmentos (4 dígitos)',
  category: 'luces',
  description: 'Cuatro dígitos de 0.56″ multiplexados: los segmentos A–G y DP son comunes y cada dígito tiene su terminal (D1–D4). Se usa con la librería SevSeg.',
  keywords: 'pantalla display 7 segmentos cuatro digitos 5641as multiplexado sevseg reloj',
  insertable: true,
  props: [commonProp, segColorProp()],
  pins: (props) => {
    const digit = props.common === 'anode' ? 'ánodo común' : 'cátodo común';
    const pin = (name, i, z) => ({
      name,
      label: `${SEG7X4_NUM[name]} · ${/^D\d$/.test(name) ? `${name} (dígito ${name[1]}, ${digit})` : SEG_LABELS[name]}`,
      kind: /^D\d$/.test(name) ? (props.common === 'anode' ? 'power' : 'gnd') : 'signal',
      x: (i - 2.5) * PITCH,
      y: 0,
      z,
      insert: true,
    });
    return [...SEG7X4_TOP.map((n, i) => pin(n, i, -SEG7X4.ROW_Z)), ...SEG7X4_BOTTOM.map((n, i) => pin(n, i, SEG7X4.ROW_Z))];
  },
  footprint: { w: SEG7X4.W, d: SEG7X4.D, h: 9 },
  help: 'Patas de arriba: D1, A, F, D2, D3, B; de abajo: E, D, DP, C, G, D4. Con SevSeg se pueden poner las resistencias en los segmentos o en los dígitos.',
});

// Módulo TM1637 (4 dígitos 0.36″ con dos puntos) ---------------------------------------------------------
export const TM1637 = {
  W: 42,
  D: 24,
  PCB_Y: 2.0,
  PCB_T: 1.2,
  display: { w: 30.0, d: 14.0, h: 7.2, x: -2.5 },
};
TM1637.TOP = TM1637.PCB_Y + TM1637.PCB_T;
TM1637.PIN_X = TM1637.W / 2 + 6.2;
TM1637.PIN_Y = TM1637.TOP + 1.27;

definePart({
  type: 'tm1637',
  name: 'Display TM1637 (4 dígitos)',
  category: 'luces',
  description: 'Módulo de 4 dígitos con dos puntos y el controlador TM1637: solo 2 cables de datos (CLK y DIO, cualquier pin). Librería TM1637Display.',
  keywords: 'pantalla tm1637 display 7 segmentos reloj 4 digitos modulo clk dio',
  props: [
    segColorProp(),
    { key: 'dots', label: 'Puntos', type: 'select', default: 'colon',
      options: [{ value: 'colon', label: 'Dos puntos (reloj)' }, { value: 'decimal', label: 'Punto decimal en cada dígito' }] },
  ],
  pins: ['CLK', 'DIO', 'VCC', 'GND'].map((name, i) => ({
    name,
    label: { CLK: 'CLK (reloj)', DIO: 'DIO (datos)', VCC: 'VCC (3.3–5 V)', GND: 'GND' }[name],
    kind: name === 'GND' ? 'gnd' : name === 'VCC' ? 'power' : 'signal',
    x: TM1637.PIN_X,
    y: TM1637.PIN_Y,
    z: (i - 1.5) * PITCH,
    dir: [1, 0, 0],
  })),
  footprint: { w: TM1637.W + 12, d: TM1637.D, h: 11, x: 6 },
  help: 'CLK y DIO tienen resistencias de 10 kΩ a VCC en el módulo. En la versión de reloj, el bit 7 del segundo dígito enciende los dos puntos. ' +
    'Este módulo no trae teclas: el chip TM1637 puede leer hasta 16 (comando 0x42, K1/K2 × SG1–SG8), pero los módulos de 4 dígitos no sacan esas patas, así que si un programa pide las teclas lee «ninguna» (0xFF).',
});

// Matrices 8×8 con MAX7219 (módulos FC-16) -------------------------------------------------------------
export const MAX7219 = {
  CELL: 32, // cada matriz (y su placa) mide 32 × 32 mm
  PCB_Y: 1.0,
  PCB_T: 1.6,
  SOCKET_H: 6.0,
  MATRIX_H: 7.5,
  PIN_OUT: 6.0, // lo que sobresalen las patas del borde de la placa
};
MAX7219.TOP = MAX7219.PCB_Y + MAX7219.PCB_T;
MAX7219.PIN_Y = MAX7219.TOP + 1.27;

const MAX_IN = ['VCC', 'GND', 'DIN', 'CS', 'CLK'];
const MAX_OUT = ['VCC.2', 'GND.2', 'DOUT', 'CS.2', 'CLK.2'];
const MAX_LABELS = {
  VCC: 'VCC (5 V)', GND: 'GND', DIN: 'DIN (datos · MOSI)', CS: 'CS (carga · LOAD)', CLK: 'CLK (reloj · SCK)',
  'VCC.2': 'VCC (salida)', 'GND.2': 'GND (salida)', DOUT: 'DOUT (al DIN del siguiente módulo)', 'CS.2': 'CS (salida)', 'CLK.2': 'CLK (salida)',
};

function maxPins(modules) {
  const half = (MAX7219.CELL * modules) / 2;
  const side = (names, sign) => names.map((name, i) => ({
    name,
    label: MAX_LABELS[name],
    kind: name.startsWith('GND') ? 'gnd' : name.startsWith('VCC') ? 'power' : 'signal',
    x: sign * (half + MAX7219.PIN_OUT),
    y: MAX7219.PIN_Y,
    z: (i - 2) * PITCH,
    dir: [sign, 0, 0],
  }));
  // La entrada queda a la derecha: el primer MAX7219 de la cadena maneja la matriz de la derecha.
  return [...side(MAX_IN, 1), ...side(MAX_OUT, -1)];
}

const MAX_BUSES = [['VCC', 'VCC.2'], ['GND', 'GND.2'], ['CS', 'CS.2'], ['CLK', 'CLK.2']];

definePart({
  type: 'max7219-matrix',
  name: 'Matriz LED 8×8 MAX7219',
  category: 'luces',
  description: 'Matriz roja de 8×8 con el controlador MAX7219 (módulo FC-16). Se maneja con 3 pines (librerías LedControl o MD_MAX72XX) y se encadena por DOUT.',
  keywords: 'pantalla max7219 matriz led 8x8 fc16 ledcontrol md_max72xx parola spi',
  pins: maxPins(1),
  buses: MAX_BUSES,
  footprint: { w: MAX7219.CELL + 2 * MAX7219.PIN_OUT, d: MAX7219.CELL, h: 17 },
  help: 'Entrada a la derecha (VCC, GND, DIN, CS, CLK) y salida a la izquierda para encadenar otro módulo (DOUT → DIN). Con MD_MAX72XX usa el tipo FC16_HW.',
});

definePart({
  type: 'max7219-matrix4',
  name: 'Matriz LED 32×8 (4 × MAX7219)',
  category: 'luces',
  description: 'Placa FC-16 con cuatro matrices 8×8 encadenadas: ideal para texto que se desplaza con MD_Parola (tipo FC16_HW).',
  keywords: 'pantalla max7219 matriz led 32x8 fc16 4 en 1 parola md_max72xx marquesina letrero texto',
  pins: maxPins(4),
  buses: MAX_BUSES,
  footprint: { w: MAX7219.CELL * 4 + 2 * MAX7219.PIN_OUT, d: MAX7219.CELL, h: 17 },
  help: 'La entrada (derecha) llega al MAX7219 de la matriz de la derecha: con MD_MAX72XX es el dispositivo 0. Con SPI por hardware: DIN al pin 11, CLK al 13 y CS a cualquier pin.',
});

// Display de 8 dígitos con MAX7219 -----------------------------------------------------------------------
export const MAX7SEG = {
  W: 82.0,
  D: 15.0,
  PCB_Y: 1.6,
  PCB_T: 1.6,
  display: { w: 30.0, d: 14.0, h: 7.2, gap: 0.6 },
  PIN_OUT: 6.0,
};
MAX7SEG.TOP = MAX7SEG.PCB_Y + MAX7SEG.PCB_T;
MAX7SEG.PIN_Y = MAX7SEG.TOP + 1.27;

definePart({
  type: 'max7219-7seg',
  name: 'Display de 8 dígitos MAX7219',
  category: 'luces',
  description: 'Ocho dígitos de 7 segmentos (dos displays de 0.36″) con el MAX7219: 3 pines de datos con LedControl (setDigit, setChar) y decodificación BCD propia del chip.',
  keywords: 'pantalla max7219 display 7 segmentos 8 digitos ledcontrol modulo spi numeros',
  props: [segColorProp()],
  pins: (() => {
    const side = (names, sign) => names.map((name, i) => ({
      name,
      label: MAX_LABELS[name],
      kind: name.startsWith('GND') ? 'gnd' : name.startsWith('VCC') ? 'power' : 'signal',
      x: sign * (MAX7SEG.W / 2 + MAX7SEG.PIN_OUT),
      y: MAX7SEG.PIN_Y,
      z: (i - 2) * PITCH,
      dir: [sign, 0, 0],
    }));
    // Entrada a la izquierda; el dígito 0 del MAX7219 es el de la derecha.
    return [...side(MAX_IN, -1), ...side(MAX_OUT, 1)];
  })(),
  buses: MAX_BUSES,
  footprint: { w: MAX7SEG.W + 2 * MAX7SEG.PIN_OUT, d: MAX7SEG.D, h: 12 },
  help: 'El dígito 0 (registro 1) es el de la derecha. Con el registro 0x09 el chip decodifica números BCD (Code B: 0–9, -, E, H, L, P).',
});

// NeoPixel (WS2812B) ---------------------------------------------------------------------------------
export const NEO_RING_SIZES = {
  12: { od: 37.0, id: 23.3 },
  16: { od: 44.5, id: 31.75 },
  24: { od: 65.6, id: 52.3 },
};
// La placa descansa sobre el conector acodado soldado por debajo (los pines salen por el borde).
export const NEO = { PCB_Y: 2.6, PCB_T: 1.6, LED: 5.0, PIN_OUT: 5.0 };
NEO.TOP = NEO.PCB_Y + NEO.PCB_T;
NEO.PIN_Y = NEO.PCB_Y - PITCH / 2;
export const NEO_STICK = { w: 51.1, d: 10.2, pitch: 5.75 };
export const NEO_MATRIX = { w: 65, d: 65, pitch: 8.1 };

// SK6812 RGBW: Adafruit vende la tira de 8 (NeoPixel Stick), los anillos de 12, 16 y 24 y la matriz de 8×8 con el LED blanco
// cálido (~3000 K), neutro (~4500 K) o frío (~6000 K); del LED de 5 mm no hay versión RGBW. Tonos: cuerpo negro.
export const NEO_WHITES = {
  'rgbw-warm': { label: 'blanco cálido (≈ 3000 K)', hex: '#ffb16e' },
  'rgbw-natural': { label: 'blanco neutro (≈ 4500 K)', hex: '#ffdabc' },
  'rgbw-cool': { label: 'blanco frío (≈ 6000 K)', hex: '#fff6ed' },
};
const neoLedsProp = {
  key: 'leds', label: 'LEDs', type: 'select', default: 'rgb',
  options: [{ value: 'rgb', label: 'RGB (WS2812B)' }, ...Object.entries(NEO_WHITES).map(([value, w]) => ({ value, label: `RGBW (SK6812), ${w.label}` }))],
};
const RGBW_NOTE = 'Con LEDs RGBW (SK6812) cada LED recibe 32 bits (G, R, B, W): en Adafruit_NeoPixel usa NEO_GRBW + NEO_KHZ800 y Color(r, g, b, w). ' +
  'FastLED no maneja el canal blanco. Si el programa manda 24 bits por LED, los colores salen corridos, como en un LED real.';

/** ¿Esta pieza NeoPixel lleva LEDs SK6812 RGBW (con canal blanco)? El LED de 5 mm no existe en RGBW. */
export function neoRgbw(type, props) {
  return type !== 'neopixel' && String((props && props.leds) || '').startsWith('rgbw');
}

const NEO_LABELS = {
  DIN: 'DIN (datos de entrada)', VCC: 'VCC (5 V)', GND: 'GND', DOUT: 'DOUT (datos de salida, al DIN del siguiente)',
  'VCC.2': 'VCC (5 V, salida)', 'GND.2': 'GND (salida)',
};
const neoKind = (name) => (name.startsWith('GND') ? 'gnd' : name.startsWith('VCC') ? 'power' : 'signal');

/** Cantidad de LEDs de cada pieza NeoPixel. */
export function neoCount(type, props) {
  if (type === 'neopixel-ring') return NEO_RING_SIZES[props.pixels] ? Number(props.pixels) : 16;
  if (type === 'neopixel-stick') return 8;
  if (type === 'neopixel-matrix') return 64;
  return 1;
}

definePart({
  type: 'neopixel',
  name: 'LED NeoPixel (WS2812) 5 mm',
  category: 'luces',
  description: 'LED RGB direccionable de 5 mm con chip integrado: un solo pin de datos controla el color (Adafruit_NeoPixel, FastLED). DOUT va al DIN del siguiente.',
  keywords: 'neopixel ws2812 ws2812b led rgb direccionable 5mm pl9823 fastled',
  insertable: true,
  props: [
    { key: 'order', label: 'Orden de colores', type: 'select', default: 'GRB',
      options: [{ value: 'GRB', label: 'GRB (WS2812D)' }, { value: 'RGB', label: 'RGB (PL9823 / APA106)' }] },
  ],
  pins: ['DIN', 'VCC', 'GND', 'DOUT'].map((name, i) => ({
    name, label: NEO_LABELS[name], kind: neoKind(name), x: (i - 1.5) * PITCH, y: 0, z: 0, insert: true,
  })),
  footprint: { w: 9, d: 6.5, h: 14 },
  help: 'Patas (con el lado plano a la izquierda): DIN, VCC, GND (la más larga) y DOUT. Pon un capacitor y una resistencia de 330 Ω en DIN en un circuito real.',
});

definePart({
  type: 'neopixel-ring',
  name: 'Anillo NeoPixel',
  category: 'luces',
  description: 'Anillo de LEDs WS2812B (12, 16 o 24) en cadena: se controla con un solo pin (Adafruit_NeoPixel o FastLED, orden GRB a 800 kHz). También con LEDs RGBW.',
  keywords: 'neopixel anillo ring ws2812b sk6812 rgbw blanco led rgb direccionable fastled',
  props: [
    { key: 'pixels', label: 'Cantidad de LEDs', type: 'select', default: 16, options: [12, 16, 24].map((n) => ({ value: n, label: String(n) })) },
    neoLedsProp,
  ],
  variants: [
    { label: 'Anillo NeoPixel 12', props: { pixels: 12 } },
    { label: 'Anillo NeoPixel 16', props: { pixels: 16 } },
    { label: 'Anillo NeoPixel 24', props: { pixels: 24 } },
    ...[12, 16, 24].map((n) => ({
      label: `Anillo NeoPixel RGBW ${n}`, props: { pixels: n, leds: 'rgbw-natural' },
      description: 'Anillo de LEDs SK6812 con canal blanco además de rojo, verde y azul (Adafruit_NeoPixel con NEO_GRBW).',
    })),
  ],
  pins: (props) => {
    const s = NEO_RING_SIZES[props.pixels] || NEO_RING_SIZES[16];
    return ['DIN', 'VCC', 'GND', 'DOUT'].map((name, i) => ({
      name, label: NEO_LABELS[name], kind: neoKind(name),
      x: (i - 1.5) * PITCH, y: NEO.PIN_Y, z: s.od / 2 + NEO.PIN_OUT, dir: [0, 0, 1],
    }));
  },
  footprint: (props) => {
    const s = NEO_RING_SIZES[props.pixels] || NEO_RING_SIZES[16];
    return { w: s.od, d: s.od + NEO.PIN_OUT, h: 6, z: NEO.PIN_OUT / 2 };
  },
  help: 'El LED 0 es el de arriba y la cuenta sigue en el sentido de las manecillas del reloj. ' + RGBW_NOTE,
});

definePart({
  type: 'neopixel-stick',
  name: 'Tira NeoPixel de 8 LEDs',
  category: 'luces',
  description: 'Barra de 8 LEDs WS2812B en cadena (Adafruit_NeoPixel o FastLED). Entrada a la izquierda y salida a la derecha para encadenar más. También con LEDs RGBW.',
  keywords: 'neopixel stick barra tira ws2812b sk6812 rgbw blanco 8 leds rgb fastled',
  props: [neoLedsProp],
  variants: [
    { label: 'Tira NeoPixel de 8 LEDs', props: {} },
    {
      label: 'Tira NeoPixel RGBW de 8 LEDs', props: { leds: 'rgbw-natural' },
      description: 'Barra de 8 LEDs SK6812 con canal blanco además de rojo, verde y azul (Adafruit_NeoPixel con NEO_GRBW).',
    },
  ],
  pins: [
    ...['DIN', 'VCC', 'GND'].map((name, i) => ({ name, label: NEO_LABELS[name], kind: neoKind(name), x: -(NEO_STICK.w / 2 + NEO.PIN_OUT), y: NEO.PIN_Y, z: (i - 1) * PITCH, dir: [-1, 0, 0] })),
    ...['DOUT', 'VCC.2', 'GND.2'].map((name, i) => ({ name, label: NEO_LABELS[name], kind: neoKind(name), x: NEO_STICK.w / 2 + NEO.PIN_OUT, y: NEO.PIN_Y, z: (i - 1) * PITCH, dir: [1, 0, 0] })),
  ],
  buses: [['VCC', 'VCC.2'], ['GND', 'GND.2']],
  footprint: { w: NEO_STICK.w + 2 * NEO.PIN_OUT, d: NEO_STICK.d, h: 6 },
  help: RGBW_NOTE,
});

definePart({
  type: 'neopixel-matrix',
  name: 'Matriz NeoPixel 8×8',
  category: 'luces',
  description: 'Panel de 64 LEDs WS2812B en cadena (Adafruit_NeoPixel, FastLED). El LED 0 está arriba a la izquierda. También con LEDs RGBW.',
  keywords: 'pantalla neopixel matriz 8x8 panel ws2812b sk6812 rgbw blanco 64 leds rgb fastled',
  props: [
    { key: 'layout', label: 'Orden de los LEDs', type: 'select', default: 'progressive',
      options: [{ value: 'progressive', label: 'Filas de izquierda a derecha' }, { value: 'serpentine', label: 'Zigzag (serpentina)' }] },
    neoLedsProp,
  ],
  variants: [
    { label: 'Matriz NeoPixel 8×8', props: {} },
    {
      label: 'Matriz NeoPixel RGBW 8×8', props: { leds: 'rgbw-natural' },
      description: 'Panel de 64 LEDs SK6812 con canal blanco además de rojo, verde y azul (Adafruit_NeoPixel con NEO_GRBW).',
    },
  ],
  pins: [
    ...['DIN', 'VCC', 'GND'].map((name, i) => ({ name, label: NEO_LABELS[name], kind: neoKind(name), x: -(NEO_MATRIX.w / 2 + NEO.PIN_OUT), y: NEO.PIN_Y, z: (i - 1) * PITCH, dir: [-1, 0, 0] })),
    ...['DOUT', 'VCC.2', 'GND.2'].map((name, i) => ({ name, label: NEO_LABELS[name], kind: neoKind(name), x: NEO_MATRIX.w / 2 + NEO.PIN_OUT, y: NEO.PIN_Y, z: (i - 1) * PITCH, dir: [1, 0, 0] })),
  ],
  buses: [['VCC', 'VCC.2'], ['GND', 'GND.2']],
  footprint: { w: NEO_MATRIX.w + 2 * NEO.PIN_OUT, d: NEO_MATRIX.d, h: 6 },
  help: 'Con 64 LEDs encendidos en blanco el panel puede pedir hasta 3.8 A: en un circuito real usa una fuente de 5 V aparte. ' + RGBW_NOTE,
});

// Barra de 10 LEDs ---------------------------------------------------------------------------------
export const BAR = { W: 25.4, D: 10.1, H: 7.9, Y0: 0.6, ROW_Z: 1.5 * PITCH };
export const BAR_COLORS = {
  red: { name: 'Roja', leds: Array(10).fill('red') },
  green: { name: 'Verde', leds: Array(10).fill('green') },
  yellow: { name: 'Amarilla', leds: Array(10).fill('yellow') },
  blue: { name: 'Azul', leds: Array(10).fill('blue') },
  white: { name: 'Blanca', leds: Array(10).fill('white') },
  multi: { name: 'Verde, amarilla y roja', leds: [...Array(5).fill('green'), ...Array(3).fill('yellow'), ...Array(2).fill('red')] },
};

definePart({
  type: 'led-bar',
  name: 'Barra de 10 LEDs',
  category: 'luces',
  description: 'Diez LEDs rectangulares en un encapsulado DIP-20 que cruza el canal de la protoboard: ánodos abajo (A1–A10) y cátodos arriba (C1–C10).',
  keywords: 'barra led bar graph grafica 10 segmentos nivel vumetro',
  insertable: true,
  props: [
    { key: 'color', label: 'Color', type: 'select', default: 'red',
      options: Object.entries(BAR_COLORS).map(([value, c]) => ({ value, label: c.name, color: LED_COLORS[c.leds[0]].hex })) },
  ],
  pins: [
    ...Array.from({ length: 10 }, (_, i) => ({
      name: 'A' + (i + 1), label: `${i + 1} · Ánodo ${i + 1} (+)`, kind: 'power', x: (i - 4.5) * PITCH, y: 0, z: BAR.ROW_Z, insert: true,
    })),
    ...Array.from({ length: 10 }, (_, i) => ({
      name: 'C' + (i + 1), label: `${20 - i} · Cátodo ${i + 1} (−)`, kind: 'gnd', x: (i - 4.5) * PITCH, y: 0, z: -BAR.ROW_Z, insert: true,
    })),
  ],
  footprint: { w: BAR.W, d: BAR.D, h: 9 },
  help: 'Cada LED necesita su resistencia (220–470 Ω). El LED 1 es el de la izquierda (pata 1 abajo a la izquierda).',
});
